"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import DateRangePicker from "./DateRangePicker";
import { GREEK_CITIES_SORTED, GREEK_CITY_REGIONS } from "@/lib/greek-cities";

async function locateAndRedirect(
  router: ReturnType<typeof useRouter>,
  onError: (msg: string) => void,
) {
  if (!("geolocation" in navigator)) {
    onError("Your browser doesn't support geolocation.");
    return;
  }
  // If the user previously denied the prompt, getCurrentPosition will
  // fail silently with a generic error and the browser won't re-ask.
  // The Permissions API lets us detect that state and show clearer
  // copy instead of looking broken.
  if ("permissions" in navigator) {
    try {
      const status = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });
      if (status.state === "denied") {
        onError(
          "Location is blocked. Allow it in your browser's site settings (the 🔒 icon next to the URL) and try again.",
        );
        return;
      }
    } catch {
      // older browsers — just fall through to getCurrentPosition.
    }
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const params = new URLSearchParams();
      params.set("lat", pos.coords.latitude.toFixed(5));
      params.set("lng", pos.coords.longitude.toFixed(5));
      router.push(`/search?${params.toString()}`);
    },
    (err) => {
      if (err.code === 1) {
        onError(
          "You blocked location access. Allow it in your browser settings to use Nearby.",
        );
      } else if (err.code === 3) {
        onError("Took too long to find your location. Try again or pick a city instead.");
      } else {
        onError("Couldn't get your location — please pick a city instead.");
      }
    },
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
  );
}

type Props = {
  defaultCity?: string;
  defaultCheckIn?: string;
  defaultCheckOut?: string;
  defaultGuests?: number;
};

const FEATURED_DESTS: { city: string; emoji: string }[] = [
  { city: "Athens", emoji: "🏛" },
  { city: "Mykonos", emoji: "⛵" },
  { city: "Santorini", emoji: "🌅" },
  { city: "Crete", emoji: "🏖" },
  { city: "Thessaloniki", emoji: "🏙" },
  { city: "Naxos", emoji: "🏝" },
  { city: "Corfu", emoji: "🌊" },
  { city: "Rhodes", emoji: "🏰" },
];

type ActiveField = "where" | "when" | "who" | null;

export default function SearchBar({
  defaultCity = "",
  defaultCheckIn,
  defaultCheckOut,
  defaultGuests = 1,
}: Props) {
  const router = useRouter();
  const [city, setCity] = useState(defaultCity);
  const [checkIn, setCheckIn] = useState(defaultCheckIn ?? "");
  const [checkOut, setCheckOut] = useState(defaultCheckOut ?? "");
  // Airbnb-style guest split. `guests` going to the search URL is
  // adults + children (the count that affects sleeping capacity).
  // Infants and pets are tracked separately so the host knows the
  // full party even if the URL doesn't carry them yet.
  const [adults, setAdults] = useState(Math.max(1, defaultGuests));
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pets, setPets] = useState(0);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveField>(null);
  const rootRef = useRef<HTMLFormElement>(null);

  // Close whichever popover is open on outside click / Escape.
  useEffect(() => {
    if (!active) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setActive(null);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setActive(null);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [active]);

  function go() {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (checkIn) params.set("checkIn", checkIn);
    if (checkOut) params.set("checkOut", checkOut);
    const guestCount = adults + children;
    if (guestCount > 0) params.set("guests", String(guestCount));
    // Send the full party breakdown so the search page's sticky bar
    // initialises the Who popover with the same values the user just
    // entered on the homepage (otherwise infants + the children/
    // adult split get lost on the redirect).
    if (adults > 0) params.set("adults", String(adults));
    if (children > 0) params.set("children", String(children));
    if (infants > 0) params.set("infants", String(infants));
    if (pets > 0) params.set("pets", String(pets));
    setActive(null);
    router.push(`/search?${params.toString()}`);
  }

  const q = city.trim().toLowerCase();
  const filtered = q
    ? GREEK_CITIES_SORTED.filter((c) => c.toLowerCase().includes(q)).slice(0, 8)
    : null;

  // Pretty values shown inside each field.
  const whenLabel = (() => {
    if (checkIn && checkOut) {
      const fmt = (s: string) =>
        new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `${fmt(checkIn)} — ${fmt(checkOut)}`;
    }
    if (checkIn) return "Pick check-out";
    return "Add dates";
  })();
  const totalAdultChild = adults + children;
  const whoLabel = (() => {
    const bits: string[] = [];
    if (totalAdultChild > 0) {
      bits.push(`${totalAdultChild} guest${totalAdultChild === 1 ? "" : "s"}`);
    }
    if (infants > 0) bits.push(`${infants} infant${infants === 1 ? "" : "s"}`);
    if (pets > 0) bits.push(`${pets} pet${pets === 1 ? "" : "s"}`);
    return bits.length > 0 ? bits.join(", ") : "Add guests";
  })();

  return (
    <form
      ref={rootRef}
      onSubmit={(e) => {
        e.preventDefault();
        go();
      }}
      className={`search${active ? ` is-${active}` : ""}`}
    >
      {/* ── WHERE ──────────────────────────────────────── */}
      <label
        className={`search-field${active === "where" ? " is-active" : ""}`}
        style={{ position: "relative" }}
      >
        <span>Where</span>
        <input
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
            setActive("where");
          }}
          onFocus={() => setActive("where")}
          placeholder="Search destinations"
          autoComplete="off"
          spellCheck={false}
        />
        {locateError ? (
          <span className="field-sub" style={{ color: "var(--warn)" }}>
            {locateError}
          </span>
        ) : null}

        {active === "where" ? (
          <div className="sb-suggest" role="listbox" onMouseDown={(e) => e.stopPropagation()}>
            {filtered == null ? (
              <>
                <button
                  type="button"
                  className="sb-suggest-row"
                  onClick={() => {
                    setLocateError(null);
                    locateAndRedirect(router, setLocateError);
                  }}
                >
                  <span className="sb-suggest-thumb sb-suggest-thumb-nearby">
                    <svg width="18" height="18" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                      <path d="M12 2L2 6.5l4.5 1.5L8 12z" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="sb-suggest-text">
                    <span className="sb-suggest-main">Nearby</span>
                    <span className="sb-suggest-sub">Find what&apos;s around you</span>
                  </span>
                </button>

                <div className="sb-suggest-header">Suggested destinations</div>
                {FEATURED_DESTS.map((d) => {
                  const region = GREEK_CITY_REGIONS[d.city];
                  return (
                    <button
                      key={d.city}
                      type="button"
                      className="sb-suggest-row"
                      onClick={() => {
                        setCity(d.city);
                        setActive("when");
                      }}
                    >
                      <span className="sb-suggest-thumb sb-suggest-thumb-emoji">{d.emoji}</span>
                      <span className="sb-suggest-text">
                        <span className="sb-suggest-main">{d.city}, Greece</span>
                        <span className="sb-suggest-sub">
                          {region ? `For ${region.toLowerCase()}-region stays` : "Greece"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </>
            ) : filtered.length === 0 ? (
              <div className="sb-suggest-empty">
                No matches — press Search to search for &ldquo;{city}&rdquo; anyway.
              </div>
            ) : (
              <>
                <div className="sb-suggest-header">Matching destinations</div>
                {filtered.map((c) => {
                  const region = GREEK_CITY_REGIONS[c];
                  return (
                    <button
                      key={c}
                      type="button"
                      className="sb-suggest-row"
                      onClick={() => {
                        setCity(c);
                        setActive("when");
                      }}
                    >
                      <span className="sb-suggest-thumb sb-suggest-thumb-pin">
                        <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5}>
                          <path d="M7 13s-4-3.5-4-7a4 4 0 018 0c0 3.5-4 7-4 7z" />
                          <circle cx="7" cy="6" r="1.5" />
                        </svg>
                      </span>
                      <span className="sb-suggest-text">
                        <span className="sb-suggest-main">{c}, Greece</span>
                        <span className="sb-suggest-sub">
                          {region ? `${region}` : "Greece"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        ) : null}
      </label>

      {/* ── WHEN ───────────────────────────────────────── */}
      <label
        className={`search-field${active === "when" ? " is-active" : ""}`}
        onClick={() => setActive("when")}
      >
        <span>When</span>
        <DateRangePicker
          variant="bare"
          valueStart={checkIn || undefined}
          valueEnd={checkOut || undefined}
          required={false}
          numberOfMonths={2}
          placeholder="Add dates"
          onChange={(s, e) => {
            setCheckIn(s);
            setCheckOut(e);
            if (s && e) setActive("who");
          }}
        />
      </label>

      {/* ── WHO ────────────────────────────────────────── */}
      <label
        className={`search-field${active === "who" ? " is-active" : ""}`}
        style={{ position: "relative" }}
        onClick={() => setActive("who")}
      >
        <span>Who</span>
        <span className={`search-field-val${totalAdultChild === 0 ? " empty" : ""}`}>
          {whoLabel}
        </span>

        {active === "who" ? (
          <div className="sb-who" onClick={(e) => e.stopPropagation()}>
            <StepperRow
              title="Adults"
              sub="Ages 13 or above"
              value={adults}
              min={1}
              onChange={(v) => setAdults(v)}
            />
            <StepperRow
              title="Children"
              sub="Ages 2 — 12"
              value={children}
              min={0}
              onChange={(v) => setChildren(v)}
            />
            <StepperRow
              title="Infants"
              sub="Under 2"
              value={infants}
              min={0}
              onChange={(v) => setInfants(v)}
            />
            <StepperRow
              title="Pets"
              sub="Check the listing's pet policy"
              value={pets}
              min={0}
              onChange={(v) => setPets(v)}
              last
            />
          </div>
        ) : null}
      </label>

      <button type="submit" className="search-btn" aria-label="Search">
        <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2}>
          <circle cx={6} cy={6} r={4} />
          <path d="M9 9l4 4" />
        </svg>
        <span className="search-btn-label">Search</span>
      </button>
    </form>
  );
}

function StepperRow({
  title,
  sub,
  value,
  min,
  onChange,
  last,
}: {
  title: string;
  sub: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
  last?: boolean;
}) {
  return (
    <div className={`sb-who-row${last ? " last" : ""}`}>
      <div>
        <div className="sb-who-title">{title}</div>
        <div className="sb-who-sub">{sub}</div>
      </div>
      <div className="sb-who-stepper">
        <button
          type="button"
          aria-label={`Decrease ${title}`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          −
        </button>
        <span aria-live="polite">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${title}`}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}
